import api from "../api/api";
import { appState } from "../redux/redux-config";

function pick(source, camelKey, pascalKey, fallback) {
  return source?.[camelKey] ?? source?.[pascalKey] ?? fallback;
}

function normalizePlan(plan) {
  return {
    ...plan,
    id: pick(plan, "id", "Id", 0),
    name: pick(plan, "name", "Name", ""),
    credits: pick(plan, "credits", "Credits", 0),
    display: pick(plan, "display", "Display", pick(plan, "name", "Name", "")),
  };
}

function hasSuccess(result) {
  return result?.success ?? result?.Success ?? (result?.errorMessage ? false : true);
}

function normalizeManageMessagePlanPage(result) {
  const allowedBillingCycles =
    pick(result, "allowedBillingCycles", "AllowedBillingCycles", []) || [];
  const plans = (pick(result, "plans", "Plans", []) || []).map(normalizePlan);

  return {
    ...result,
    success: hasSuccess(result),
    customerId: pick(result, "customerId", "CustomerId", 0),
    productId: pick(result, "productId", "ProductId", 0),
    billingModeId: pick(result, "billingModeId", "BillingModeId", 0),
    accountStatus: pick(result, "accountStatus", "AccountStatus", ""),
    isFreeTrialCustomer: pick(result, "isFreeTrialCustomer", "IsFreeTrialCustomer", false),
    trialDaysRemaining: pick(result, "trialDaysRemaining", "TrialDaysRemaining", 0),
    currentBillingCycle: pick(result, "currentBillingCycle", "CurrentBillingCycle", ""),
    currentPlanId: pick(result, "currentPlanId", "CurrentPlanId", 0),
    currentPlanDisplay: pick(result, "currentPlanDisplay", "CurrentPlanDisplay", ""),
    selectedBillingCycle: pick(result, "selectedBillingCycle", "SelectedBillingCycle", ""),
    selectedPlanId: pick(result, "selectedPlanId", "SelectedPlanId", null),
    selectedPlanDisplay: pick(result, "selectedPlanDisplay", "SelectedPlanDisplay", null),
    allowedBillingCycles,
    plans,
    canEdit: pick(result, "canEdit", "CanEdit", false),
    showNewBillingText: pick(result, "showNewBillingText", "ShowNewBillingText", false),
    showLegacyBillingText: pick(result, "showLegacyBillingText", "ShowLegacyBillingText", false),
    effectiveDateMessage: pick(result, "effectiveDateMessage", "EffectiveDateMessage", ""),
    annualNotice: pick(result, "annualNotice", "AnnualNotice", null),
  };
}

function normalizePlanOptions(result) {
  const plans = (pick(result, "plans", "Plans", []) || []).map(normalizePlan);

  return {
    ...result,
    success: hasSuccess(result),
    billingCycle: pick(result, "billingCycle", "BillingCycle", ""),
    plans,
    selectedPlanId: pick(result, "selectedPlanId", "SelectedPlanId", null),
    selectedPlanDisplay: pick(result, "selectedPlanDisplay", "SelectedPlanDisplay", null),
    message: pick(result, "message", "Message", null),
    canSave: pick(result, "canSave", "CanSave", false),
  };
}

function normalizePlanPreview(result) {
  return {
    ...result,
    success: hasSuccess(result),
    currentBillingCycle: pick(result, "currentBillingCycle", "CurrentBillingCycle", ""),
    currentPlanDisplay: pick(result, "currentPlanDisplay", "CurrentPlanDisplay", ""),
    newBillingCycle: pick(result, "newBillingCycle", "NewBillingCycle", ""),
    newPlanId: pick(result, "newPlanId", "NewPlanId", null),
    newPlanDisplay: pick(result, "newPlanDisplay", "NewPlanDisplay", null),
    requiresRedirectToPurchaseCredits: pick(
      result,
      "requiresRedirectToPurchaseCredits",
      "RequiresRedirectToPurchaseCredits",
      false,
    ),
    canSave: pick(result, "canSave", "CanSave", false),
    validationErrors: pick(result, "validationErrors", "ValidationErrors", []) || [],
  };
}

function normalizePlanChange(result) {
  return {
    ...result,
    success: hasSuccess(result),
    message: pick(result, "message", "Message", ""),
    redirectUrl: pick(result, "redirectUrl", "RedirectUrl", null),
    updatedBillingCycle: pick(result, "updatedBillingCycle", "UpdatedBillingCycle", ""),
    updatedPlanId: pick(result, "updatedPlanId", "UpdatedPlanId", 0),
    updatedPlanDisplay: pick(result, "updatedPlanDisplay", "UpdatedPlanDisplay", ""),
  };
}

const manageMessagePlan = {
  getPage: async function () {
    let token = appState().user.token;
    let result = await api.manageMessagePlan.getPage(token);
    return normalizeManageMessagePlanPage(result || {});
  },
  getPlans: async function (billingCycle) {
    let token = appState().user.token;
    let result = await api.manageMessagePlan.getPlans(token, billingCycle);
    return normalizePlanOptions(result || {});
  },
  preview: async function (data) {
    let token = appState().user.token;
    return normalizePlanPreview(await api.manageMessagePlan.preview(token, data) || {});
  },
  change: async function (data) {
    let token = appState().user.token;
    return normalizePlanChange(await api.manageMessagePlan.change(token, data) || {});
  },
};

export default manageMessagePlan;
